'use client';

import { useEffect } from 'react';
import { useUserStore } from '@/store/userStore';
import { useAuthStore } from '@/store/authStore';

const USER_PROFILE_STORAGE_KEY = 'user-profile-storage';
const DEFAULT_NAME = 'User Name';
const DEFAULT_EMAIL = 'user@example.com';

/**
 * Syncs persisted store state into Zustand on client mount so the navbar
 * shows profile and username immediately after login redirect (full page load).
 * Persist middleware rehydrates async; this triggers rehydration early and
 * also syncs user profile from localStorage if the store still has defaults.
 */
export default function StoreRehydrationSync() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const log = (msg: string, data?: object) =>
      console.log('[SYNC_DEBUG]', msg, data ?? '');

    log('StoreRehydrationSync effect started');

    const userBefore = useUserStore.getState();
    const authBefore = useAuthStore.getState();
    log('Store state before rehydrate', {
      userName: userBefore.name,
      userPhoto: !!userBefore.photo,
      hasAccessToken: !!authBefore.accessToken,
      hasRefreshToken: !!authBefore.refreshToken,
    });

    // Trigger rehydration as soon as app mounts so stores get persisted state early
    const userPersist = useUserStore.persist;
    const authPersist = useAuthStore.persist;
    if (userPersist?.rehydrate) {
      userPersist.rehydrate();
      log('userPersist.rehydrate() called');
    }
    if (authPersist?.rehydrate) {
      authPersist.rehydrate();
      log('authPersist.rehydrate() called');
    }

    // Sync user profile from localStorage into store if store still has defaults
    try {
      const raw = localStorage.getItem(USER_PROFILE_STORAGE_KEY);
      log('localStorage user-profile', {
        hasRaw: !!raw,
        rawLength: raw?.length ?? 0,
      });
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        state?: {
          name?: string;
          email?: string;
          id?: string;
          freshworksGuestId?: string;
          mobile?: string;
          photo?: string;
        };
      };
      const state = parsed?.state;
      if (!state || !state.id) {
        log('Skip sync: no state or id');
        return;
      }
      const hasRealProfile =
        (state.name && state.name !== DEFAULT_NAME) ||
        (state.email && state.email !== DEFAULT_EMAIL);
      if (!hasRealProfile) {
        log('Skip sync: no real profile');
        return;
      }
      const currentName = useUserStore.getState().name;
      const willSync = currentName === DEFAULT_NAME;
      log('Profile sync decision', {
        currentName,
        storedName: state.name,
        willCallSetUserProfile: willSync,
      });
      if (willSync) {
        useUserStore
          .getState()
          .setUserProfile(
            state.name || DEFAULT_NAME,
            state.email || DEFAULT_EMAIL,
            state.id || '',
            state.freshworksGuestId || '',
            state.mobile || '',
            state.photo
          );
        log('setUserProfile called', {
          name: state.name,
          hasPhoto: !!state.photo,
        });
      }
    } catch (e) {
      log('Sync error', { error: e });
    }
  }, []);

  return null;
}
